"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
    assertSameIdentity,
    clearAuthMark,
    getCurrentUser,
    SESSION_CHANGED_EVENT,
    serverLogout,
    silentRefresh,
} from "@/lib/auth";
import { UserContext, AssignmentContext } from "@/lib/permissions";

interface WorkspaceState {
    user: UserContext | null;
    activeContext: AssignmentContext | null;
    isLoading: boolean;
    setActiveContext: (context: AssignmentContext | null) => void;
    refreshUser: () => Promise<void>;
    logout: () => void;
}

const WorkspaceContext = createContext<WorkspaceState | undefined>(undefined);

/** Routes that are reached *without* a session and must never redirect to /login. */
const PUBLIC_AUTH_ROUTES = ["/login", "/logged-out", "/sso"];

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<UserContext | null>(null);
    const [activeContext, setActiveContext] = useState<AssignmentContext | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const pathname = usePathname();
    // Kept in a ref, updated in an effect: `refreshUser` reads it and is a
    // dependency of the mount effect, so taking the router straight from the
    // hook would give it a fresh identity every render and re-run that effect
    // in a loop.
    const router = useRouter();
    const routerRef = React.useRef(router);
    const pathRef = React.useRef(pathname);
    useEffect(() => {
        routerRef.current = router;
        pathRef.current = pathname;
    }, [router, pathname]);

    /**
     * True when this user has no claim on the group in the current URL.
     *
     * Used only after an identity swap: the workspace on screen belongs to
     * whoever was signed in a moment ago, and staying on their group page means
     * every panel fetches a 403 it was never written to render.
     */
    /** The profile as last applied to `user`, serialised. Compared against on
     *  every re-read so an unchanged profile leaves the object alone — see
     *  `refreshUser`. */
    const lastProfileRef = React.useRef<string>("");

    const strandedOnAnotherUsersGroup = useCallback((profile: UserContext | null) => {
        const groupMatch = (pathRef.current || "").match(/\/groups\/([^\/]+)\/(\d+)/);
        if (!groupMatch || !profile) return false;
        const [, entityType, entityId] = groupMatch;
        if (["admin", "superadmin", "staff"].includes((profile.role || "").toLowerCase())) return false;
        const hasAssignment = (profile.assignments || []).some(
            (a: AssignmentContext) =>
                a.entity_type.toLowerCase() === entityType.toLowerCase() &&
                String(a.entity_id) === String(entityId),
        );
        if (hasAssignment) return false;

        // A temporary event helper has no assignment on the group by design, but
        // does have a claim on this one event. Without this they'd be bounced to
        // /dashboard the instant they opened the event link a chair sent them.
        // Scoped to the meeting id in the URL, so the grant never reads as
        // general access to the rest of the group.
        const meetingMatch = (pathRef.current || "").match(/\/meetings\/([0-9a-fA-F-]{36})/);
        if (meetingMatch) {
            const isHelperHere = (profile.event_helper_grants || []).some(
                (g) =>
                    g.meeting_id === meetingMatch[1] &&
                    g.entity_type.toLowerCase() === entityType.toLowerCase() &&
                    String(g.entity_id) === String(entityId),
            );
            if (isHelperHere) return false;
        }
        return true;
    }, []);

    /**
     * Re-read the signed-in profile.
     *
     * `silent` decides whether this is allowed to blank the app. Consumers gate
     * their whole page on `isLoading` (`if (wsLoading || loading) return
     * <LoadingScreen />`), so flipping it tears the current page down and
     * remounts it — every panel loses its state and refetches. That is correct
     * on first load, when there is nothing on screen to lose, and wrong for a
     * background re-check: returning to the tab was throwing away a half-filled
     * admissions checklist and reloading the whole group page.
     *
     * A silent refresh still swaps the user and still redirects on an identity
     * swap — it just does not claim the app is loading while it asks.
     *
     * Silence is not enough on its own, though. `getCurrentUser` parses fresh
     * JSON, so writing it into state hands every consumer a new *object* for
     * the same person. Effects keyed on `user` re-run, and the group page's
     * loader re-runs with `setLoading(true)` — which blanks the page behind a
     * LoadingScreen and remounts the panel anyway. So an unchanged profile
     * must not be written at all: the object identity is the thing that tears
     * the page down, not the loading flag.
     */
    const refreshUser = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const profile = await getCurrentUser();
            if (profile) {
                // Ensure assignments is an array
                profile.assignments = profile.assignments || [];
                // Signing in as someone else in another tab shares the cookie
                // with this one, so the profile that comes back can be a
                // different person from the one this tab is rendering.
                const swapped = assertSameIdentity(profile);
                const serialised = JSON.stringify(profile);
                // Nothing about this person changed — keep the object the app
                // is already rendering. An identity swap is never skipped.
                if (!swapped && serialised === lastProfileRef.current) return;
                lastProfileRef.current = serialised;
                setUser(profile as UserContext);
                if (swapped) {
                    setActiveContext(null);
                    if (strandedOnAnotherUsersGroup(profile as UserContext)) {
                        routerRef.current.replace("/dashboard");
                    }
                }
            } else {
                lastProfileRef.current = "";
                setUser(null);
                setActiveContext(null);
            }
        } catch (error) {
            console.error("Failed to fetch user in WorkspaceContext", error);
            lastProfileRef.current = "";
            setUser(null);
            setActiveContext(null);
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [strandedOnAnotherUsersGroup]);

    const logout = useCallback(async () => {
        await serverLogout();  // revokes refresh token in DB, clears cookies server-side
        clearAuthMark();
        setUser(null);
        setActiveContext(null);
        // Members sign in via CMT Association SSO, so send them to a logout
        // landing page that points them back there (staff can still use /login).
        window.location.href = "/logged-out";
    }, []);

    /** One place decides what a dead session does, so the timer below and the
     *  event listener above it cannot drift apart.
     *
     *  Signing out is not a failure, so the pages you land on with no session —
     *  /login, /logged-out, the SSO handshake — must never be bounced to /login
     *  for having none. `window.location.href` is a full navigation, so doing it
     *  from /login reloads /login, which re-mounts this provider, which asks for
     *  the profile again: the page reloads forever and the sign-in form is wiped
     *  out from under whoever is typing into it. */
    const sessionDied = useCallback(() => {
        setUser(null);
        setActiveContext(null);
        if (PUBLIC_AUTH_ROUTES.some(r => (pathRef.current || "").startsWith(r))) return;
        window.location.href = "/login";
    }, []);

    useEffect(() => {
        void refreshUser();
    }, [refreshUser]);

    /**
     * React when the session underneath this tab changes.
     *
     * Two different signals for two different problems:
     *   - `fetchWithAuth` fires SESSION_CHANGED_EVENT on a terminal 401. The
     *     session is dead; there is nothing to re-hydrate.
     *   - `visibilitychange` catches the two-tab case, where nothing in this tab
     *     has failed yet: the user signed in as somebody else next door and is
     *     coming back to a workspace that is no longer theirs. Re-fetching the
     *     profile on return is what turns that from a crash into a redirect.
     */
    useEffect(() => {
        const onSessionChange = () => sessionDied();
        const onVisible = () => {
            // Silent: this fires every time the tab is brought back, and a
            // loud refresh would remount whatever page is open. See refreshUser.
            if (document.visibilityState === "visible") void refreshUser({ silent: true });
        };
        window.addEventListener(SESSION_CHANGED_EVENT, onSessionChange);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener(SESSION_CHANGED_EVENT, onSessionChange);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [refreshUser, sessionDied]);

    // Proactive token refresh — rotate the access token every 55 minutes so it
    // never expires during an active session. The access token lives for 1 hour;
    // refreshing at 55 min gives a 5-minute safety margin.
    useEffect(() => {
        const REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes
        const id = setInterval(async () => {
            const ok = await silentRefresh();
            // Refresh token expired or revoked — session is dead
            if (!ok) sessionDied();
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [sessionDied]);

    // Context Auto-Detection Hook
    useEffect(() => {
        if (!user || !user.assignments || user.assignments.length === 0) return;

        // Pattern matches public group views and management routes
        // e.g. /groups/committee/35 or /dashboard/manage/groups/committee/35/edit
        const groupMatch = pathname.match(/\/groups\/([^\/]+)\/(\d+)/);

        if (groupMatch) {
            const [, entityType, entityId] = groupMatch;
            const matchingAssignment = user.assignments.find((a: AssignmentContext) => 
                a.entity_type.toLowerCase() === entityType.toLowerCase() && 
                String(a.entity_id) === String(entityId)
            );
            
            if (matchingAssignment) {
                // Determine if they are accessing a management route
                if (pathname.includes("/dashboard/manage/groups")) {
                    // Check if they hold a leadership role to be in a management route
                    const role = (matchingAssignment.role || "").toLowerCase();
                    if (role.includes("chair") || role.includes("leader")) {
                        setActiveContext(matchingAssignment);
                    } else if (user.role && ["admin", "superadmin"].includes(user.role.toLowerCase())) {
                        setActiveContext(matchingAssignment);
                    } else {
                        // Normally they shouldn't be here, but we still set context 
                        // so PermissionGate accurately denies them
                        setActiveContext(matchingAssignment);
                    }
                } else {
                    // Public route view for the group
                    setActiveContext(matchingAssignment);
                }
            } else {
                // They navigated to a group they aren't part of, reset context to global
                // unless they are an admin
                if (user.role && ["admin", "superadmin"].includes(user.role.toLowerCase())) {
                    // Admins get fake context to manage everything
                    setActiveContext({
                        assignment_id: "99999", // dummy
                        entity_type: entityType,
                        entity_id: Number(entityId),
                        role: user.role,
                        title: `${entityType} ${entityId} (Admin View)`
                    });
                } else {
                    setActiveContext(null);
                }
            }
        } else {
            // Not in a group specific view, revert to Global Overview
            setActiveContext(null);
        }
    }, [pathname, user]);

    const contextValue = useMemo<WorkspaceState>(() => ({
        user,
        activeContext,
        isLoading,
        setActiveContext,
        refreshUser,
        logout,
    }), [user, activeContext, isLoading, setActiveContext, refreshUser, logout]);

    return (
        <WorkspaceContext.Provider value={contextValue}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error("useWorkspace must be used within a WorkspaceProvider");
    }
    return context;
}
